import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Stack,
  InputAdornment,
  IconButton,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import logo from "../../assets/logo_transparent_black.png";
import { forceChangePassword } from "../../api/user";
import { useAuthStore } from "../../authProvider";
import { validatePassword } from "../../utils/passwordValidation";

export const ForceChangePassword = () => {
  const navigate = useNavigate();
  const { setUser } = useAuthStore();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!validatePassword(newPassword)) {
      setError("Password does not meet security requirements");
      return;
    }

    setIsLoading(true);

    try {
      const response = await forceChangePassword({
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      if (response.data?.status === "SUCCESS" && response.data?.data?.user) {
        // Update user in auth store with email_confirmed = true
        setUser(response.data.data.user);

        // Redirect to profile or intended page
        const returnUrl = new URLSearchParams(window.location.search).get(
          "returnUrl"
        );
        navigate(returnUrl || "/profile");
      } else {
        setError(response.data?.message || "Failed to change password");
      }
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(
        error.response?.data?.message ||
          "An error occurred while changing password"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container
      sx={{
        width: "100%",
        maxWidth: "450px !important",
        height: "85vh",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
          <img
            src={logo}
            alt="Fashion Words Logo"
            style={{
              maxWidth: "150px",
              height: "auto",
              objectFit: "contain",
            }}
          />
        </Box>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: "100%",
            backgroundColor: "primary.dark",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              pt: 2,
              pb: 4,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                textAlign: "center",
                color: "primary.light",
                fontSize: "1.75rem",
                fontWeight: "bold",
                mb: 2,
              }}
            >
              Change Your Password
            </Typography>

            <Typography
              variant="h2"
              sx={{
                textAlign: "center",
                color: "primary.light",
                fontSize: "1rem",
              }}
            >
              For security reasons, please change your temporary password.
            </Typography>
          </Box>
          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <Box>
                <TextField
                  fullWidth
                  id="new-password"
                  label="New Password"
                  type={showNewPassword ? "text" : "password"}
                  variant="filled"
                  required
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setError(null);
                  }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                          >
                            {showNewPassword ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: "primary.light",
                    fontSize: "0.75rem",
                    mt: 0.5,
                    display: "block",
                  }}
                >
                  Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 symbol
                </Typography>
              </Box>

              <TextField
                fullWidth
                id="confirm-password"
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                variant="filled"
                required
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setError(null);
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              {error && (
                <Typography
                  sx={{
                    color: "primary.light",
                    fontSize: "0.875rem",
                    textAlign: "center",
                    pb: 1,
                    borderBottom: "4px solid #D0021B",
                  }}
                >
                  {error}
                </Typography>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{
                  mt: 2,
                  py: 1.5,
                }}
              >
                {isLoading ? "Changing Password..." : "Change Password"}
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};
